import { Box, Card, Typography } from "@mui/material";
import { type FC, useMemo } from "react";
import { COLORS, ROLE_GROUPS } from "../types";

interface MissingByRole {
	total: number;
	byRole: Record<string, number>;
}

interface HiringSummaryCardsProps {
	census: number;
	targetHppd: number;
	shiftStats: {
		byRole: Record<string, { recurring: number; pickup: number; overtime: number; agency: number }>;
		totalShifts: number;
	};
	predictedHppd: number;
	missingShifts: number | MissingByRole;
	callOffCount: number;
	ptoDaily: number;
}

/** Helper to get the color for a missing value */
const getMissingColor = (value: number): string => {
	if (value > 0) return COLORS.error;
	if (value < 0) return COLORS.warning;
	return COLORS.success;
};

export const HiringSummaryCards: FC<HiringSummaryCardsProps> = ({ census, targetHppd, shiftStats, predictedHppd, missingShifts, callOffCount, ptoDaily }) => {
	// Normalize missingShifts to always have byRole structure
	const missingData: MissingByRole = typeof missingShifts === "number" ? { total: missingShifts, byRole: {} } : missingShifts;

	const actualHppd = useMemo(() => {
		const totalShifts = shiftStats.totalShifts || 0;
		const daysInWeek = 7;
		const callOffRateLocal = totalShifts === 0 ? 0 : Math.min((callOffCount * daysInWeek) / totalShifts, 1);
		const ptoRateLocal = totalShifts === 0 ? 0 : Math.min((ptoDaily * daysInWeek) / totalShifts, 1);
		const combinedRate = Math.min(callOffRateLocal + ptoRateLocal, 1);
		const actualHppdLocal = predictedHppd * (1 - combinedRate);

		return actualHppdLocal;
	}, [shiftStats.totalShifts, callOffCount, ptoDaily, predictedHppd]);

	const smallCards = [
		{
			label: "Census",
			value: census,
		},
		{
			label: "Target HPPD",
			value: targetHppd,
		},
	];

	const dotItems = [
		{ label: "Recurring", key: "recurring" as const, color: COLORS.primary },
		{ label: "Pickup", key: "pickup" as const, color: COLORS.success },
		{ label: "Overtime", key: "overtime" as const, color: COLORS.warning },
		{ label: "Agency", key: "agency" as const, color: COLORS.error },
	];

	return (
		<Box sx={{ display: "flex", gap: 2, flexWrap: { xs: "wrap", md: "nowrap" } }}>
			{/* Weekly Shifts - large card */}
			<Card sx={{ p: 2, border: "1px solid #E1E6EF", boxShadow: "none", borderRadius: "8px", flex: { xs: "1 1 100%", md: "2 1 0" } }}>
				<Box sx={{ display: "flex", gap: 1 }}>
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
						<Typography variant="subtitle2" sx={{ color: COLORS.primary, fontWeight: 600, mb: 1 }}>
							Weekly Shifts
						</Typography>
						<Typography sx={{ fontSize: "24px", fontWeight: 700, color: "primary.dark" }}>{shiftStats.totalShifts}</Typography>
					</Box>
					{ROLE_GROUPS.map((role) => {
						const roleStats = shiftStats.byRole[role];
						return (
							<Box key={role} sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", minWidth: 0 }}>
								<Typography sx={{ fontSize: "13px", fontWeight: 600, color: "primary.dark", mb: 1 }}>{role}</Typography>
								<Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.75 }}>
									{dotItems.map((item) => (
										<Box key={item.key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
											<Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: item.color, flexShrink: 0 }} />
											<Typography sx={{ fontSize: "12px", color: "primary.dark", whiteSpace: "nowrap" }}>
												{item.label} <b>{roleStats[item.key]}</b>
											</Typography>
										</Box>
									))}
								</Box>
							</Box>
						);
					})}
				</Box>
			</Card>

			{/* Small metric cards (read-only parameters) */}
			{smallCards.map((card) => (
				<Card
					key={card.label}
					sx={{
						p: 1.25,
						border: "1px solid #E1E6EF",
						boxShadow: "none",
						borderRadius: "8px",
						flex: { xs: "1 1 calc(50% - 16px)", md: "0.8 1 0" },
						minWidth: 80,
						position: "relative",
					}}
				>
					<Typography sx={{ color: COLORS.primary, fontWeight: 600, mb: 0.5 }}>{card.label}</Typography>
					<Typography sx={{ fontSize: "16px", fontWeight: 700 }}>{String(card.value)}</Typography>
				</Card>
			))}

			<Card
				sx={{
					p: 1.25,
					border: "1px solid #E1E6EF",
					boxShadow: "none",
					borderRadius: "8px",
					flex: { xs: "1 1 calc(50% - 16px)", md: "0.8 1 0" },
					minWidth: 80,
					position: "relative",
				}}
			>
				<Typography sx={{ color: COLORS.primary, fontWeight: 600, mb: 0.5 }}>Predicted HPPD</Typography>
				<Typography sx={{ fontSize: "16px", fontWeight: 700, color: COLORS.primary }}>{predictedHppd.toFixed(2)}</Typography>
				<Box sx={{ mt: 0.5 }}>
					<Typography sx={{ fontSize: "14px", fontWeight: 700, color: COLORS.primary }}>Actual HPPD: {actualHppd.toFixed(2)}</Typography>
				</Box>
			</Card>

			{/* Missing Shifts Card */}
			<Card sx={{ p: 1.5, border: "1px solid #E1E6EF", boxShadow: "none", borderRadius: "8px" }}>
				<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25 }}>
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 0.25 }}>
						<Typography sx={{ fontSize: "12px", fontWeight: 600, color: COLORS.primary, mb: 0.25 }}>Missing Shifts</Typography>
						<Typography sx={{ fontSize: "18px", fontWeight: 700, color: getMissingColor(missingData.total), mb: 0 }}>{missingData.total}</Typography>
					</Box>
					{ROLE_GROUPS.map((role) => {
						const roleValue = missingData.byRole[role] ?? 0;
						const fte = Math.ceil(roleValue / 3);
						return (
							<Box key={role} sx={{ display: "flex", flexDirection: "row", alignItems: "center", minWidth: 0, gap: 0.5, py: 0.1 }}>
								<Typography sx={{ fontSize: "12px", fontWeight: 600, color: "primary.dark", minWidth: 90 }}>
									{role} FTE: {fte}
								</Typography>
							</Box>
						);
					})}
				</Box>
			</Card>
		</Box>
	);
};
